import {
  Avatar,
  Button,
  ButtonBase,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { useLocalization } from 'localization';
import { CaretRightIcon } from 'components/v2/icons/CaretRightIcon';
import LoadingText from 'components/v2/text/LoadingText';
import { GearIcon } from 'components/v2/icons/GearIcon';
import { FacebookIcon } from 'components/v2/icons/FacebookIcon';
import { InstagramIcon } from 'components/v2/icons/InstagramIcon';
import { colors } from 'theme/mui/colors';
import routes from 'app.routes';
import FetchingErrorAlert from 'components/v2/alert/FetchingErrorAlert';
import QUERIES from 'modules/common/queries';
import SkeletonContainer from 'components/v2/container/SkeletonContainer';
import { useGetBranchInformationByIdApi } from 'api/branches/query';
import PersonIcon from 'components/v2/icons/PersonIcon';
import { useCurrentBranch } from 'hooks/services/useCurrentBranch';
import { SocialMediaPlatform } from 'constants/enums/general';

const DashboardAdsAccount = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { clientStates, apiStates, actions } = useCurrentBranch();
  const { isError, userIsLoading, userName } = apiStates;
  const [currentPage, setCurrentPage] = useState(1);

  const { data: branchData, isLoading: isBranchLoading } = useGetBranchInformationByIdApi(
    clientStates?.currentSelectedBranch?.id ?? ''
  );
  const itemsPerPage = 2;

  const socialMedias = branchData?.socialMedias ?? [];
  const totalPages = Math.max(1, Math.ceil(socialMedias.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages]);

  const start = (currentPage - 1) * itemsPerPage;
  const pageItems = socialMedias.slice(start, start + itemsPerPage);

  const socialMediaIcons: Record<string, React.ReactNode> = {
    [SocialMediaPlatform.FACEBOOK]: (
      <FacebookIcon
        style={{
          width: '2rem',
          height: '2rem',
          aspectRatio: '1/1',
        }}
      />
    ),
    [SocialMediaPlatform.INSTAGRAM]: (
      <InstagramIcon
        style={{
          width: '2rem',
          height: '2rem',
          aspectRatio: '1/1',
        }}
      />
    ),
  };

  return (
    <Card
      sx={{
        width: '100%',
        minHeight: '30rem',
        justifyContent: isError || userIsLoading ? 'center' : 'unset',
        alignItems: isError || userIsLoading ? 'center' : 'unset',
      }}
    >
      {!isError ? (
        <SkeletonContainer loading={userIsLoading || isBranchLoading} rows={{ xs: 1 }} columns={{ xs: 1 }}>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1 0 0',
              alignSelf: 'stretch',
            }}
          >
            <CardMedia
              component="img"
              image={branchData?.profile?.coverImageUrl ?? '/img/default-banner.jpg'}
              alt="coverImage"
              height={180}
              sx={{
                maxHeight: 'fit-content',
                minHeight: 180,
              }}
            />
            <Stack
              sx={{
                mt: '-2rem',
                pl: '0.5rem',
                pr: '0.5rem',
              }}
              gap="0.75rem"
            >
              <Stack
                direction="row"
                alignSelf="stretch"
                justifyContent="space-between"
                alignItems="flex-end"
                pl="0.5rem"
                pr="0.5rem"
              >
                {branchData?.profile?.avatarImageUrl ? (
                  <Avatar
                    src={branchData?.profile?.avatarImageUrl}
                    alt="User avatar"
                    sx={{
                      objectFit: 'cover',
                      width: '6rem',
                      height: '6rem',
                      borderRadius: '50%',
                      border: '2px solid white',
                    }}
                  />
                ) : (
                  <Stack
                    sx={{
                      borderRadius: '50%',
                      backgroundColor: 'grey.300',
                      color: 'text.secondary',
                      border: '2px solid white',
                    }}
                  >
                    <PersonIcon
                      style={{
                        objectFit: 'cover',
                        width: '6rem',
                        height: '6rem',
                      }}
                    />
                  </Stack>
                )}
                <Button
                  variant="outlined"
                  sx={{ width: 'fit-content' }}
                  onClick={() => navigate(routes.v2.accountSettings.branchSetting)}
                  endIcon={<GearIcon />}
                >
                  {t('common.terms.settings')}
                </Button>
              </Stack>
              <Stack gap="0.5rem">
                <Typography variant="h5">{clientStates?.currentSelectedBranch?.name ?? '-'}</Typography>
                <LoadingText
                  loading={userIsLoading}
                  text="common.terms.greetingUser"
                  additionalText={{ name: userName ?? '' }}
                />
              </Stack>
            </Stack>
          </CardContent>
          <CardActions
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
          >
            {pageItems?.length ? (
              <>
                <Stack
                  sx={{
                    width: '100%',
                    pl: '0.5rem',
                  }}
                >
                  <Typography variant="body2">{t('accountSetting.connectedAccounts')}</Typography>
                </Stack>
                {pageItems.map(socialMedia => (
                  <Card sx={{ width: '100%' }} key={socialMedia.id}>
                    <Button
                      variant="text"
                      sx={{
                        color: colors.gray[600],
                        ml: '0 !important',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                      startIcon={socialMediaIcons[socialMedia.platform]}
                      endIcon={<CaretRightIcon />}
                      onClick={() => open(socialMedia.url, '_blank')}
                    >
                      <Stack width="100%" alignItems="flex-start">
                        <Typography variant="body2">{socialMedia.platform}</Typography>
                        <Typography
                          variant="h6"
                          sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {socialMedia.url}
                        </Typography>
                      </Stack>
                    </Button>
                  </Card>
                ))}
              </>
            ) : null}
            {totalPages > 1 && (
              <Stack direction="row" spacing={1} mt={1.25} alignItems="center" justifyContent="center">
                {Array.from({ length: totalPages }, (_, i) => {
                  const page = i + 1;
                  const selected = page === currentPage;
                  return (
                    <ButtonBase
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: selected ? colors.red[100] : colors.gray[300],
                      }}
                    />
                  );
                })}
              </Stack>
            )}
          </CardActions>
        </SkeletonContainer>
      ) : (
        <FetchingErrorAlert queryKey={[[QUERIES.user]]} isLoading={userIsLoading} onRetry={actions.refetch} />
      )}
    </Card>
  );
};

export default DashboardAdsAccount;
